export interface NormalizedTaskListUnitStructure {
    taskLists: Record<string, any>;
    taskListsOrder: string[];
}

const isRecord = (value: unknown): value is Record<string, any> => {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
};

const uniqueStrings = (values: unknown[]): string[] => {
    const result: string[] = [];
    const seen = new Set<string>();

    for (const value of values) {
        if (typeof value !== 'string' || seen.has(value)) {
            continue;
        }
        seen.add(value);
        result.push(value);
    }

    return result;
};

export function normalizeTaskListUnitStructure(
    taskListsValue: unknown,
    taskListsOrderValue: unknown,
): NormalizedTaskListUnitStructure {
    const sourceTaskLists = isRecord(taskListsValue) ? taskListsValue : {};
    const taskLists: Record<string, any> = {};

    for (const [listId, rawList] of Object.entries(sourceTaskLists)) {
        const list = isRecord(rawList) ? rawList : {};
        taskLists[listId] = {
            ...list,
            id: listId,
            childIds: uniqueStrings(Array.isArray(list.childIds) ? list.childIds : []),
        };
    }

    const taskListsOrder = uniqueStrings(Array.isArray(taskListsOrderValue) ? taskListsOrderValue : [])
        .filter(id => taskLists[id] !== undefined);

    for (const listId of Object.keys(taskLists)) {
        if (!taskListsOrder.includes(listId)) {
            taskListsOrder.push(listId);
        }
    }

    return {taskLists, taskListsOrder};
}

export function mergeTaskListsByCurrentState(
    currentTaskListsValue: unknown,
    intendedTaskListsValue: unknown,
): Record<string, any> {
    const currentTaskLists = isRecord(currentTaskListsValue) ? currentTaskListsValue : {};
    const intendedTaskLists = isRecord(intendedTaskListsValue) ? intendedTaskListsValue : {};
    const merged: Record<string, any> = {};

    for (const [listId, currentList] of Object.entries(currentTaskLists)) {
        const intendedList = isRecord(intendedTaskLists[listId]) ? intendedTaskLists[listId] : {};
        const currentShape = isRecord(currentList) ? currentList : {};
        merged[listId] = {
            ...currentShape,
            ...intendedList,
            id: listId,
            childIds: Array.isArray(intendedList.childIds)
                ? intendedList.childIds
                : Array.isArray(currentShape.childIds)
                    ? currentShape.childIds
                    : [],
        };
    }

    for (const [listId, intendedListValue] of Object.entries(intendedTaskLists)) {
        if (merged[listId] !== undefined) {
            continue;
        }

        const intendedList = isRecord(intendedListValue) ? intendedListValue : {};
        merged[listId] = {
            ...intendedList,
            id: listId,
            childIds: Array.isArray(intendedList.childIds)
                ? intendedList.childIds
                : [],
        };
    }

    return merged;
}
